import {useState} from "react";
import {LoaderCircle, RefreshCw} from "lucide-react";
import {trpc} from "@/admin/lib/trpc";

type Trial = {subscriptionId: string; organizationName: string; version: string; status: string; expiresAt: string | null};
function dateInput(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function TrialRenewalPanel({trial, onClose, onBusy}: {trial: Trial; onClose: () => void; onBusy: (busy: boolean) => void}) {
  const utils = trpc.useUtils();
  const active = trial.status === "trialing" && (!trial.expiresAt || Date.parse(trial.expiresAt) > Date.now());
  const action = active ? "extend" : "reactivate";
  const base = Math.max(Date.now(), trial.expiresAt ? Date.parse(trial.expiresAt) : 0);
  const [expiresOn, setExpiresOn] = useState(dateInput(new Date(base + 30 * 86400000)));
  const [error, setError] = useState("");
  const [authorizationReady, setAuthorizationReady] = useState(false);
  const renewal = trpc.admin.renewLaunchedTrial.useMutation({
    onMutate: () => onBusy(true),
    onSettled: () => onBusy(false),
    onSuccess: async result => {setAuthorizationReady(result.apiAuthorizationReady); await utils.admin.listLaunchedTrials.invalidate();},
  });
  const refresh = trpc.admin.refreshLaunchedTrialAuthorization.useMutation({onSuccess: () => setAuthorizationReady(true)});
  const result = renewal.data;
  const credentials = result ? [
    ...(result.apiKey ? [`API key: ${result.apiKey}`] : []),
    ...(result.s3 ? [`S3 access key: ${result.s3.accessKeyId}`, `S3 secret key: ${result.s3.secretAccessKey}`] : []),
  ].join("\n") : "";
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const selectedDate = new FormData(event.currentTarget).get("expiresOn");
    const date = new Date(`${selectedDate ?? ""}T23:59:59.999`);
    if (!Number.isFinite(date.getTime())) {setError("Choose an expiry date."); return;}
    setError("");
    renewal.mutate({subscriptionId: trial.subscriptionId, action, expiresAt: date});
  };
  return <section className="trial-renewal-panel" aria-label={`${active ? "Extend" : "Reactivate"} ${trial.organizationName} trial`}>
    <h3>{result ? "Trial renewed" : `${active ? "Extend" : "Reactivate"} trial`}</h3>
    {result ? <>
      <p role="status">{trial.organizationName} retains {trial.version} access until {new Date(result.expiresAt).toLocaleString("en-GB", {dateStyle: "long", timeStyle: "short"})}.</p>
      {credentials ? <div className="trial-renewal-credentials">
        <strong>Save the replacement credentials now</strong>
        <p>Share these with the trial recipient. They are shown once. Previously revoked credentials remain revoked.</p>
        <label htmlFor={`trial-credentials-${trial.subscriptionId}`}>Replacement credentials</label>
        <textarea id={`trial-credentials-${trial.subscriptionId}`} readOnly value={credentials} rows={result.s3 ? 4 : 2} spellCheck={false} onFocus={event => event.target.select()} />
      </div> : <p>The existing API and S3 credentials are preserved.</p>}
      {!authorizationReady ? <div role="alert">
        <p>The new expiry is saved. API authorization still needs to refresh.</p>
        <button type="button" disabled={refresh.isPending} onClick={() => refresh.mutate({subscriptionId: trial.subscriptionId})}><RefreshCw aria-hidden="true" />{refresh.isPending ? "REFRESHING…" : "RETRY API REFRESH"}</button>
        {refresh.error ? <p className="active-trials-error">{refresh.error.message}</p> : null}
      </div> : <p>API authorization is ready.</p>}
      <button type="button" onClick={onClose}>{credentials ? "I HAVE SAVED THE CREDENTIALS" : "DONE"}</button>
    </> : <form onSubmit={submit}>
      <p>Keep {trial.version} and the current access options. Existing credentials are preserved where possible; replacements for revoked credentials appear here after reactivation.</p>
      <label htmlFor={`trial-expiry-${trial.subscriptionId}`}>New expiry date</label>
      <input id={`trial-expiry-${trial.subscriptionId}`} name="expiresOn" type="date" required min={dateInput(new Date())} max={dateInput(new Date(Date.now() + 365 * 86400000))} value={expiresOn} disabled={renewal.isPending} onChange={event => setExpiresOn(event.target.value)} />
      <p>Access ends at 23:59 on this date in your local time ({Intl.DateTimeFormat().resolvedOptions().timeZone}).</p>
      <div className="trial-renewal-actions">
        {[30, 90].map(days => <button key={days} type="button" disabled={renewal.isPending} onClick={() => setExpiresOn(dateInput(new Date(base + days * 86400000)))}>+{days} DAYS</button>)}
        <button type="button" disabled={renewal.isPending} onClick={onClose}>CANCEL</button>
        <button className="is-primary" type="submit" disabled={renewal.isPending}>{renewal.isPending ? <LoaderCircle className="active-trials-spin" aria-hidden="true" /> : <RefreshCw aria-hidden="true" />}{renewal.isPending ? "UPDATING ACCESS…" : active ? "EXTEND TRIAL" : "REACTIVATE TRIAL"}</button>
      </div>
      {error || renewal.error ? <p className="active-trials-error" role="alert">{error || renewal.error?.message}</p> : null}
    </form>}
  </section>;
}
